import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import React, { memo } from 'react'
import { ContentEditorProps } from '../../ContentEditor'
import { withLazyLoad } from '../../withLazyComponent'
import { KeyedText } from './KeyedText'

const ContentEditor = withLazyLoad<ContentEditorProps>(() =>
    import('../../ContentEditor').then((module) => ({
        default: module.ContentEditor,
    }))
)

/** Props for the text editor component */
interface EditorContentProps extends ContentEditorProps {
    /** Whether this text has a label prefix (like "Title:") */
    isKeyedText: boolean
    /** The label text that appears before the editable portion */
    keyedText: string
    /** Function to set the editing state */
    setIsEditingPlainText: (value: boolean) => void
    /** The editable text portion after the label */
    keyedTextValue?: string
    /** CSS styling classes */
    classes?: string
}

/**
 * A text editor that handles two display formats:
 * 1. Regular text editing (standard mode)
 * 2. Label-value pairs where the label (with semicolon) is fixed and only the value is editable
 *
 * Content team members always see the standard editor regardless of format.
 *
 * @param {EditorContentProps} props - Props for the component.
 */
export const EditorContent = memo<EditorContentProps>(
    ({
        initialData,
        initialSuggestions,
        clickCoordinates,
        handlers,
        setIsEditingPlainText,
        isKeyedText,
        keyedText,
        keyedTextValue,
        classes,
    }) => {
        const isContentTeamFromStudioPage = useShallowSelector(
            (state) => state.isContentTeamFromStudioPage
        )

        const editorComponent = (
            <ContentEditor
                initialData={initialData}
                initialSuggestions={initialSuggestions}
                clickCoordinates={clickCoordinates}
                handlers={{
                    onReady: handlers?.onReady,
                    onBlur: handlers?.onBlur,
                    onChange: handlers?.onChange,
                }}
            />
        )

        // show standard editor, for regular text or from OM/SMM content
        if (!isKeyedText || isContentTeamFromStudioPage) {
            return editorComponent
        }

        // show label separately from editable value, for keyed text
        return (
            <>
                <KeyedText
                    keyedText={keyedText}
                    classes={classes}
                    data-testid="keyed-text-key"
                />
                {keyedTextValue && editorComponent}
            </>
        )
    }
)
